import { forwardRef, useImperativeHandle } from 'react';
import * as React from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import { TranslationService } from '../../../services/translation.service';
import RequiredAsterisk from '../RequiredAsterisk';
import ValidationInfo from '../../../validation/validation-info';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import { IdHelper } from '../../../helpers/id.helper';

export interface ITimePickerState {
  value: string;
  validationInfo: ValidationInfo;
}

export interface ITimePickerProps {
  hourStep?: number;
  minuteStep?: number;
  showAsteriskOnRequiredFields?: boolean;
  ariaDescribedBy?: string | undefined;
  labelText?: string;
  className?: string | undefined;
  serverValidationName?: string | undefined;
  state: ITimePickerState;
  onChange?: ((time: string) => void) | undefined;
}

function readableNumber(i: number): string {
  if (i < 10) {
    return `0${i}`;
  }
  return `${i}`;
}

function createOptionsArray(step: number, max: number): [number, string][] {
  const result: [number, string][] = [];
  for (let i = 0; i < max; i += step) {
    result.push([i, readableNumber(i)]);
  }
  return result;
}

function parseHoursAndMinutesFromTimeString(time: string): [number, number] {
  if (/^\d{2}:\d{2}$/.test(time) === false) {
    return [DateTime.now().hour, DateTime.now().minute];
  }

  const [hoursString, minutesString] = time.split(':');
  const [hours, minutes] = [parseInt(hoursString, 10), parseInt(minutesString, 10)];

  if (Number.isNaN(hours) || hours < 0 || hours > 23 || Number.isNaN(minutes) || minutes < 0 || minutes > 59) {
    return [DateTime.now().hour, DateTime.now().minute];
  }

  return [hours, minutes];
}

function roundToNearestStep([hours, minutes]: [number, number], hourStep: number, minuteStep: number): [number, number] {
  const roundedHours = Math.floor(hours / hourStep) * hourStep;
  const roundedMinutes = Math.floor(minutes / minuteStep) * minuteStep;
  return [roundedHours, roundedMinutes];
}

export default forwardRef<IValidatable, ITimePickerProps>(function TimePicker(
  { hourStep = 1, minuteStep = 5, showAsteriskOnRequiredFields = false, ariaDescribedBy, labelText, className, serverValidationName = 'time', state, onChange }: ITimePickerProps,
  ref
) {
  // === CONSTANTS ===
  const timeElementId = IdHelper.newUniqueId('time-select-');
  const hourElementId = IdHelper.newUniqueId('hour-select-');
  const minuteElementId = IdHelper.newUniqueId('minute-select-');

  // === VARIABLES ===
  const hourOptions = createOptionsArray(hourStep, 24);
  const minuteOptions = createOptionsArray(minuteStep, 60);
  const labelTextWithDefault = labelText ?? TranslationService.get('common.time');
  const [selectedHour, selectedMinute] = roundToNearestStep(parseHoursAndMinutesFromTimeString(state.value), hourStep, minuteStep);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      return new ValidationInfo(`${labelTextWithDefault} - `, []);
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelTextWithDefault} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  // === EVENT HANDLERS ===
  function handleMobileTimeInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const [newHour, newMinute] = parseHoursAndMinutesFromTimeString(e.target.value);
    onChange?.(`${readableNumber(newHour)}:${readableNumber(newMinute)}`);
  }

  function handleHourSelectChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const newHour = parseInt(e.target.value, 10);
    onChange?.(`${readableNumber(newHour)}:${readableNumber(selectedMinute)}`);
  }

  function handleMinuteSelectChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const newMinute = parseInt(e.target.value, 10);
    onChange?.(`${readableNumber(selectedHour)}:${readableNumber(newMinute)}`);
  }

  return (
    <div className={`timepicker w-100 d-flex ${className ?? ''}`}>
      <div className="w-100 d-block d-sm-none">
        <label htmlFor={timeElementId} className="form-label">
          {labelTextWithDefault}
          {showAsteriskOnRequiredFields && <RequiredAsterisk />}
        </label>
        <input
          id={timeElementId}
          value={state.value}
          onChange={handleMobileTimeInputChange}
          required
          className={`form-control rounded timepicker__mobile-time ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
          type="time"
          aria-invalid={state.validationInfo.isInvalid ? 'true' : 'false'}
        />
      </div>
      <div className="w-50 me-1 d-none d-sm-block">
        <label htmlFor={hourElementId} className="form-label">
          {TranslationService.get('common.hour')}
          {showAsteriskOnRequiredFields && <RequiredAsterisk />}
        </label>
        <select
          id={hourElementId}
          value={selectedHour}
          onChange={handleHourSelectChange}
          className={`form-select ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
          aria-describedby={ariaDescribedBy}
          aria-invalid={state.validationInfo.isInvalid ? 'true' : 'false'}
        >
          {hourOptions.map(([val, h]) => (
            <option key={h} className={selectedHour === val ? 'selected' : ''} value={val}>
              {h}
            </option>
          ))}
        </select>
      </div>
      <div className="w-50 ms-1 d-none d-sm-block">
        <label htmlFor={minuteElementId} className="form-label">
          {TranslationService.get('common.minute')}
          {showAsteriskOnRequiredFields && <RequiredAsterisk />}
        </label>
        <select
          id={minuteElementId}
          value={selectedMinute}
          onChange={handleMinuteSelectChange}
          className={`form-select ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
          aria-describedby={ariaDescribedBy}
          aria-invalid={state.validationInfo.isInvalid ? 'true' : 'false'}
        >
          {minuteOptions.map(([val, m]) => (
            <option key={m} className={selectedMinute === val ? 'selected' : ''} value={val}>
              {m}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
});
